import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../../services/api';

// Get stored auth data
const token = localStorage.getItem('token');
const user = JSON.parse(localStorage.getItem('user') || 'null');

export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await authAPI.login(credentials);
    const { token, data } = response.data;
    
    // Ensure permissions are properly formatted
    const user = data.user;
    if (user.permissions && typeof user.permissions === 'string') {
      user.permissionsArray = user.permissions.split(',').map(p => p.trim()).filter(p => p);
    } else if (Array.isArray(user.permissions)) {
      user.permissionsArray = user.permissions;
    } else {
      user.permissionsArray = [];
    }
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    console.log('Login successful:', {
      email: user.email,
      type: user.type,
      source: user.source,
      role: user.primaryRole?.code || user.role,
      permissions: user.permissionsArray?.slice(0, 5) // Log first 5 permissions
    });
    
    return { token, user };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Login failed');
  }
});

export const getProfile = createAsyncThunk('auth/getProfile', async (_, { rejectWithValue }) => {
  try {
    const response = await authAPI.getProfile();
    const userData = response.data.data;
    
    // Ensure permissions are properly formatted
    if (userData.permissions && typeof userData.permissions === 'string') {
      userData.permissionsArray = userData.permissions.split(',').map(p => p.trim()).filter(p => p);
    } else if (Array.isArray(userData.permissions)) {
      userData.permissionsArray = userData.permissions;
    } else {
      userData.permissionsArray = [];
    }
    
    // Update localStorage with fresh data
    localStorage.setItem('user', JSON.stringify(userData));
    
    return userData;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to get profile');
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  return null;
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user,
    token,
    isAuthenticated: !!token,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    forceLogout: (state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = 'Session expired. Please login again.';
    },
    updateUser: (state, action) => {
      // Only update if there are actual changes
      const hasChanges = Object.keys(action.payload).some(
        key => state.user?.[key] !== action.payload[key]
      );
      if (hasChanges) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });
  },
});

export const { clearError, forceLogout, updateUser } = authSlice.actions;
export default authSlice.reducer;
